import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Flex, Form, Tree} from 'antd'
import InputText from '../common/InputText'

const MAX_VALUE_LENGTH = 60

const isObjectLike = (value) => value !== null && typeof value === 'object'

const formatPrimitive = (value) => {
  if (value === null) return 'null'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

// Build a raw node tree from JSON data.
// Nodes: { key, keyText, valueText, valueType, children }
const buildTreeNodes = (value, path, key) => {
  const nodeKey = path.join('-')

  if (isObjectLike(value)) {
    const entries = Array.isArray(value)
      ? value.map((item, index) => [index, item])
      : Object.entries(value)

    if (entries.length === 0) {
      return {
        key: nodeKey,
        keyText: key,
        valueText: Array.isArray(value) ? '[]' : '{}',
        valueType: 'empty',
        children: [],
      }
    }

    // Arrays of primitive values (e.g. harness levels) become a parent node
    // with a single comma-separated child, so long lists don't produce huge
    // trees and the values stay visible as a child of their key.
    if (Array.isArray(value) && entries.every(([, item]) => !isObjectLike(item))) {
      return {
        key: nodeKey,
        keyText: key,
        valueText: `${entries.length}`,
        valueType: 'array',
        children: [
          {
            key: `${nodeKey}-values`,
            keyText: undefined,
            valueText: entries.map(([, item]) => formatPrimitive(item)).join(', '),
            valueType: 'string-list',
            children: [],
          },
        ],
      }
    }

    // Objects whose every value is a primitive (e.g. the connectors/devices
    // ref->name maps) get the same treatment as arrays of primitives: collapse
    // into a single comma-separated child instead of one leaf per entry.
    if (
      !Array.isArray(value) &&
      entries.every(([, item]) => !isObjectLike(item))
    ) {
      return {
        key: nodeKey,
        keyText: key,
        valueText: `${entries.length}`,
        valueType: 'object',
        children: [
          {
            key: `${nodeKey}-values`,
            keyText: undefined,
            valueText: entries
              .map(([childKey, item]) => `${childKey}: ${formatPrimitive(item)}`)
              .join(', '),
            valueType: 'kv-list',
            pairs: entries.map(([childKey, item]) => ({
              key: childKey,
              value: formatPrimitive(item),
            })),
            children: [],
          },
        ],
      }
    }

    return {
      key: nodeKey,
      keyText: key,
      valueText: `${entries.length}`,
      valueType: Array.isArray(value) ? 'array' : 'object',
      children: entries.map(([childKey, childValue], index) =>
        buildTreeNodes(
          childValue,
          [...path, index],
          Array.isArray(value) ? undefined : childKey
        )
      ),
    }
  }

  return {
    key: nodeKey,
    keyText: key,
    valueText: formatPrimitive(value),
    valueType:
      value === null
        ? 'null'
        : typeof value === 'number'
          ? 'number'
          : typeof value === 'boolean'
            ? 'boolean'
            : 'string',
    children: [],
  }
}

const jsonToTreeData = (obj) => {
  if (obj === null || typeof obj !== 'object') return []
  const entries = Array.isArray(obj)
    ? obj.map((item, index) => [index, item])
    : Object.entries(obj)
  return entries.map(([key, value], index) =>
    buildTreeNodes(value, [index], key)
  )
}

const highlight = (text, lower) => {
  if (!lower || !text) return text
  const idx = text.toLowerCase().indexOf(lower)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <span className="json-tree-search-highlight">
        {text.slice(idx, idx + lower.length)}
      </span>
      {text.slice(idx + lower.length)}
    </>
  )
}

const truncate = (text) =>
  text.length > MAX_VALUE_LENGTH
    ? `${text.slice(0, MAX_VALUE_LENGTH)}…`
    : text

const buildTitle = (node, lower) => {
  const { keyText, valueText, valueType } = node
  // Comma-separated lists (e.g. harness levels) are important data and must
  // always be shown in full; only long scalar values get truncated.
  const isList = valueType === 'string-list'
  const displayValue = isList ? valueText : truncate(valueText)

  // Key/value pairs (e.g. connectors/devices ref->name maps) keep the
  // pre-collapse styling: each pair is a bold key + value badge with a comma
  // separator between pairs and a hover highlight on each pair.
  if (valueType === 'kv-list') {
    const pairsLength = node.pairs.length-1
    return (
      <span className="json-tree-kv-list">
        {node.pairs.map((pair, index) => (
          <Fragment key={`${node.key}-pair-${index}`}>
            {/* {index > 0 && <span className="json-tree-kv-separator">, </span>} */}
            <span className="json-tree-kv-pair">
              <span className="json-tree-node-key">
                {highlight(pair.key, lower)}<>: </>
              </span>
              <span className="json-tree-value-badge">
                {highlight(pair.value, lower)} 
              </span>
              {index < pairsLength && <>, </>}
            </span>
          </Fragment>
        ))}
      </span>
    )
  }

  return (
    <span className="json-tree-node-title">
      {keyText ? (
        <span className="json-tree-node-key">
          {highlight(keyText, lower)}
        </span>
      ) : null}
      <span
        title={displayValue !== valueText ? valueText : undefined}
        className={`json-tree-value-badge${isList ? ' json-tree-value-badge--list' : ''}`}
      >
        {highlight(displayValue, lower)}
      </span>
    </span>
  )
}

// Filter the raw nodes to matches (plus ancestor branches) when a query is
// present. When a node itself matches, its full subtree is kept.
const toTreeData = (nodes, lower, keepAll = false) =>
  nodes.reduce((acc, node) => {
    const searchText = `${node.keyText ?? ''} ${node.valueText ?? ''}`.toLowerCase()
    const selfMatch = keepAll || (lower ? searchText.includes(lower) : true)
    const children = node.children?.length
      ? toTreeData(node.children, lower, selfMatch)
      : []
    if (!keepAll && lower && !selfMatch && children.length === 0) return acc
    acc.push({
      key: node.key,
      title: buildTitle(node, lower),
      ...(node.children?.length && children.length ? { children } : {}),
    })
    return acc
  }, [])

const collectKeys = (node) => [
  node.key,
  ...(node.children?.length ? node.children.flatMap(collectKeys) : []),
]

const JsonTreeSearch = ({ jsonData }) => {
  const intl = useIntl()
  const [searchValue, setSearchValue] = useState('')
  const [expandedKeys, setExpandedKeys] = useState([])
  const [autoExpandParent, setAutoExpandParent] = useState(true)

  const rawTree = useMemo(() => jsonToTreeData(jsonData), [jsonData])
  const lower = searchValue.trim().toLowerCase()
  const treeData = useMemo(() => toTreeData(rawTree, lower), [rawTree, lower])
  const treeKeys = useMemo(() => treeData.flatMap(collectKeys), [treeData])

  useEffect(() => {
    setExpandedKeys(lower ? treeKeys : [])
    setAutoExpandParent(true)
  }, [lower, treeKeys])

  return (
    <Flex
      vertical
      gap={8}
      className="json-tree-search"
      style={{ width: '100%' }}
    >
      <Form
        component="div"
        className="json-tree-search-form"
        onValuesChange={(_, values) => setSearchValue(values.treeSearch || '')}
      >
        <InputText
          type="search"
          name="treeSearch"
          placeholder={intl.formatMessage({
            id: 'connectorViews.list.searchTreePlaceholder',
          })}
          isRequired={false}
          wrapperClass="search-wrapper"
        />
      </Form>
      <div className="expanded-json-tree">
        <Tree
          treeData={treeData}
          showLine
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          onExpand={(keys) => {
            setExpandedKeys(keys)
            setAutoExpandParent(false)
          }}
        />
      </div>
    </Flex>
  )
}

export default JsonTreeSearch
